'use client';
import { FlexibleFormVariant } from './FlexibleFormBase';
import { getMicroservicesCountryCode } from '@/services/microservices/usedCarEligibility';
import { submitFlepzRequest } from '@/services/microservices/flepz';
import { updateUseCase } from '@/services/microservices/useCase';
import {
    clearFlexibleFormSubmitError,
    setFlexibleFormSubmitError,
} from './flexibleFormShared';
import { findFlexibleFormField } from './flexibleFormFieldMatching';
import { buildFlepzRedirectUrl } from './flepzResponseHandler';

interface RequiredFlepzFieldNames {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    zipCode?: string;
}

const FLEPZ_GENERIC_ERROR =
    'We could not process your request right now. Please verify your details and try again.';

const FLEPZ_OPT_IN_FOR_NFL = false;
const FLEPZ_TRIAL_ONLY = false;
const FLEPZ_PROSPECT_TRIAL = false;

export function getRequiredFlepzFieldNames(formFields: SiteFormField[]): RequiredFlepzFieldNames {
    const firstNameField = findFlexibleFormField(
        formFields,
        ({ fieldName, displayName, label }) =>
            fieldName === 'firstname' || displayName === 'firstname' || label === 'firstname',
    );
    const lastNameField = findFlexibleFormField(
        formFields,
        ({ fieldName, displayName, label }) =>
            fieldName === 'lastname' || displayName === 'lastname' || label === 'lastname',
    );
    const emailField = findFlexibleFormField(
        formFields,
        ({ type, fieldName, displayName, label }) =>
            type === 'emailonly' ||
            type === 'emailconfirmation' ||
            fieldName === 'email' ||
            displayName === 'email' ||
            label === 'email',
    );
    const phoneField = findFlexibleFormField(
        formFields,
        ({ type, fieldName, displayName, label }) =>
            type === 'phone' || fieldName === 'phone' || displayName === 'phone' || label === 'phone',
    );
    const zipCodeField = findFlexibleFormField(
        formFields,
        ({ type, fieldName, displayName, label }) =>
            type === 'zipcode' ||
            fieldName === 'zipcode' ||
            fieldName === 'zip' ||
            displayName === 'zipcode' ||
            displayName === 'zip' ||
            displayName.includes('zip') ||
            label === 'zipcode' ||
            label === 'zip' ||
            label.includes('zip'),
    );

    return {
        firstName: firstNameField?.fields.field_name,
        lastName: lastNameField?.fields.field_name,
        email: emailField?.fields.field_name,
        phoneNumber: phoneField?.fields.field_name,
        zipCode: zipCodeField?.fields.field_name,
    };
}

export function createFlepzCustomSubmit(
    flepzFieldNames: RequiredFlepzFieldNames | null,
): FlexibleFormVariant['customSubmit'] {
    return async (data, context) => {
        try {
            if (
                !flepzFieldNames ||
                !flepzFieldNames.firstName ||
                !flepzFieldNames.lastName ||
                !flepzFieldNames.email ||
                !flepzFieldNames.phoneNumber ||
                !flepzFieldNames.zipCode
            ) {
                setFlexibleFormSubmitError(
                    context,
                    'This form is missing required field configuration. Please try again later.',
                );
                return;
            }

            const firstName = ((data.get(flepzFieldNames.firstName) as string) || '').trim();
            const lastName = ((data.get(flepzFieldNames.lastName) as string) || '').trim();
            const email = ((data.get(flepzFieldNames.email) as string) || '').trim();
            const phoneNumber = ((data.get(flepzFieldNames.phoneNumber) as string) || '').trim();
            const zipCode = ((data.get(flepzFieldNames.zipCode) as string) || '').trim();
            const countryCode = getMicroservicesCountryCode();

            await updateUseCase(countryCode, {
                keepCustomerInfo: false,
                // Matches the legacy "Streaming Flepz" widget's observed update-usecase payload.
                useCase: 'STREAMING_ONBOARDING',
            });

            const response = await submitFlepzRequest(countryCode, {
                firstName,
                lastName,
                email,
                phoneNumber,
                zipCode,
                optInForNFL: FLEPZ_OPT_IN_FOR_NFL,
                trialOnly: FLEPZ_TRIAL_ONLY,
                prospectTrial: FLEPZ_PROSPECT_TRIAL,
            });

            if (response.status === 'SUCCESS') {
                clearFlexibleFormSubmitError(context);

                const redirectUrl = buildFlepzRedirectUrl({
                    firstName,
                    lastName,
                    phoneNumber,
                    zipCode,
                    email,
                });

                if (redirectUrl) {
                    window.location.assign(redirectUrl);
                    return;
                }

                context.setSubmitState('success');
            } else {
                setFlexibleFormSubmitError(context, FLEPZ_GENERIC_ERROR);
            }
        } catch (error) {
            console.error('FLEPZ submit flow failed', error);
            setFlexibleFormSubmitError(context, FLEPZ_GENERIC_ERROR);
        }
    };
}
